import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/use-toast';
import { ArrowLeft, AlertCircle, Trash2, ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useGuild } from '../hooks/useGuild';
import { useGuildInvites } from '../hooks/useGuildInvites';
import { useGuildBans } from '../hooks/useGuildBans';
import { useGuildMembers } from '../hooks/useGuildMembers';
import { useGuildRoles } from '../hooks/useGuildRoles';
import { useGuildPermissions, canOpenGuildManagement } from '../hooks/useGuildPermissions';
import { useGuildChannels } from '../hooks/useGuildChannels';
import { GuildSettingsForm } from '../components/GuildSettingsForm';
import { GuildInviteManager } from '../components/GuildInviteManager';
import { GuildBanList } from '../components/GuildBanList';
import { GuildRolesManager } from '../components/GuildRolesManager';
import { GuildMemberRolesDialog } from '../components/GuildMemberRolesDialog';
import { GuildMemberList } from '../components/GuildMemberList';
import { CreateGuildChannelDialog } from '../components/CreateGuildChannelDialog';
import { TransferOwnershipCard } from '../components/TransferOwnershipCard';
import { GuildPluginSharingCard } from '../components/GuildPluginSharingCard';
import { GuildDataSpaceCard } from '../components/GuildDataSpaceCard';
import type { GuildChannelSummary, GuildMemberResponse } from '@/lib/api';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useAuth } from '@/auth';

export function GuildManagementPage() {
  const { guildId } = useParams<{ guildId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserId = user?.userId ?? null;
  const { guild, loading, error, update, remove, refresh } = useGuild(guildId);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    invites,
    loading: invitesLoading,
    create: createInvite,
    revoke: revokeInvite,
  } = useGuildInvites(guildId);
  const {
    bans,
    loading: bansLoading,
    error: bansError,
    unban: handleUnban,
  } = useGuildBans(guildId);
  const {
    members,
    total: membersTotal,
    hasMore: hasMoreMembers,
    loadMore: loadMoreMembers,
    refresh: refreshMembers,
    updateRole,
    removeMember,
  } = useGuildMembers(guildId);
  const {
    roles,
    loading: rolesLoading,
    createRole,
    updateRole: updateCustomRole,
    deleteRole,
    assignRole,
    unassignRole,
  } = useGuildRoles(guildId);
  const {
    can,
    loading: permissionsLoading,
    error: permissionsError,
    refresh: refreshPermissions,
  } = useGuildPermissions(guildId);
  const {
    channels,
    loading: channelsLoading,
    error: channelsError,
    createChannel,
    deleteChannel,
  } = useGuildChannels(guildId);
  const queryClient = useQueryClient();
  const [rolesTarget, setRolesTarget] = useState<GuildMemberResponse | null>(null);
  // The members payload already carries each member's custom roles, so the
  // dialog reads from there rather than issuing a second per-member fetch.
  const assignedRoleIds = rolesTarget
    ? (members.find((m) => m.userId === rolesTarget.userId)?.roles ?? []).map((r) => r.roleId)
    : [];
  const [createChannelOpen, setCreateChannelOpen] = useState(false);
  const [ownershipOpen, setOwnershipOpen] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState<GuildChannelSummary | null>(null);
  const [isDeletingChannel, setIsDeletingChannel] = useState(false);

  // Permissions gate the whole page, so an unresolved fetch must read as
  // "still loading", not as a denial flashing at an admin on cold load.
  if (loading || permissionsLoading) {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !guild) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error || 'Guild not found'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // A failed permissions fetch is not a denial — offer a retry instead of
  // permanently locking a legitimate admin out.
  if (permissionsError) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">{permissionsError}</p>
            <Button type="button" variant="outline" onClick={() => refreshPermissions()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isOwner = guild.myRole === 'owner';

  const handleTransferOwnership = async (newOwnerId: string) => {
    if (!guildId) return;
    await api.transferGuildOwnership(guildId, newOwnerId);
    // The transfer demotes the caller to admin, so their resolved permissions
    // and the "my guilds" grouping are both stale alongside the guild itself.
    await Promise.all([
      refresh(),
      refreshMembers(),
      queryClient.invalidateQueries({ queryKey: queryKeys.guilds.permissions(guildId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.guilds.my() }),
    ]);
    toast({ title: 'Ownership transferred' });
  };

  const handleDeleteChannel = async () => {
    if (!channelToDelete) return;
    setIsDeletingChannel(true);
    try {
      await deleteChannel(channelToDelete.channelId);
      toast({ title: 'Channel deleted' });
      setChannelToDelete(null);
    } catch (err) {
      toast({
        title: 'Could not delete channel',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDeletingChannel(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await remove();
      toast({
        title: 'Guild deleted',
        description: `“${guild.name}” has been archived.`,
      });
      setConfirmDeleteOpen(false);
      navigate('/a/arche_guilds');
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete guild',
        description: err instanceof Error ? err.message : 'Please try again.',
      });
      setIsDeleting(false);
    }
  };

  // Any management permission opens the page; each tab gates its own controls.
  // Base-role-only here would lock out someone granted e.g. `manage_roles`.
  // Shared with the detail page's "Manage" button so the two can't drift.
  const canManageAnything = canOpenGuildManagement(can);
  if (!canManageAnything) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">You don't have permission to manage this guild.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to={`/a/arche_guilds/${guild.guildId}`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to {guild.name}
        </Link>
        <h1 className="text-2xl font-semibold">Manage Guild</h1>
      </div>

      <Tabs defaultValue="settings">
        <TabsList>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="roles">Roles</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="channels">Channels</TabsTrigger>
          <TabsTrigger value="invites">Invites</TabsTrigger>
          {can('ban_members') && <TabsTrigger value="bans">Bans</TabsTrigger>}
        </TabsList>

        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <GuildSettingsForm guild={guild} onSave={update} />
            </CardContent>
          </Card>

          <GuildDataSpaceCard guild={guild} />
          <GuildPluginSharingCard guild={guild} />

          {isOwner && (
            /* Collapsed by default: neither action is routine, and both are hard
               to walk back, so they shouldn't sit open under the everyday
               settings inviting a stray click. */
            <Collapsible open={ownershipOpen} onOpenChange={setOwnershipOpen}>
              <Card className="border-destructive/50">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="w-full text-left cursor-pointer rounded-lg"
                    aria-label="Ownership and deletion"
                  >
                    <CardHeader className="flex-row items-center justify-between space-y-0">
                      <div className="space-y-1.5">
                        <CardTitle className="text-base text-destructive">
                          Ownership &amp; deletion
                        </CardTitle>
                        <CardDescription>
                          Hand the guild to someone else, or delete it.
                        </CardDescription>
                      </div>
                      <ChevronDown
                        aria-hidden="true"
                        className={`h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform ${
                          ownershipOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </CardHeader>
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-6">
                    <TransferOwnershipCard
                      members={members}
                      hasMore={hasMoreMembers}
                      onLoadMore={loadMoreMembers}
                      currentUserId={currentUserId}
                      onTransfer={handleTransferOwnership}
                    />
                    <div className="border-t pt-6 space-y-2">
                      <p className="text-sm text-muted-foreground">
                        Deleting archives the guild for every member. Active invites are revoked and
                        it can no longer be joined or browsed.
                      </p>
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => setConfirmDeleteOpen(true)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete guild
                      </Button>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}
        </TabsContent>

        <TabsContent value="roles">
          <Card>
            <CardContent className="pt-6">
              <GuildRolesManager
                roles={roles}
                loading={rolesLoading}
                canManage={can('manage_roles')}
                onCreate={createRole}
                onUpdatePermissions={(roleId, permissions) =>
                  updateCustomRole(roleId, { permissions })
                }
                onDelete={deleteRole}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="members">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Members</CardTitle>
              <CardDescription>
                Change a member’s base role, grant custom roles, or remove them.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <GuildMemberList
                  members={members}
                  myRole={guild.myRole}
                  can={can}
                  currentUserId={currentUserId}
                  onUpdateRole={async (userId, role) => {
                    await updateRole(userId, role);
                    refreshMembers();
                  }}
                  onRemove={async (userId) => {
                    await removeMember(userId);
                    refreshMembers();
                  }}
                  onManageRoles={can('manage_roles') ? setRolesTarget : undefined}
                />
              </div>
              {hasMoreMembers && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={loadMoreMembers}
                >
                  Load more (showing {members.length} of {membersTotal})
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="channels">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Channels</CardTitle>
              <CardDescription>The guild’s chat channels.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {channelsLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : channelsError ? (
                <p className="text-sm text-destructive">{channelsError}</p>
              ) : channels.length === 0 ? (
                <p className="text-sm text-muted-foreground">No channels yet.</p>
              ) : (
                <ul className="divide-y">
                  {channels.map((channel) => (
                    <li key={channel.channelId} className="flex items-center gap-2 py-2">
                      <span className="flex-1 min-w-0 truncate text-sm">
                        # {channel.name}
                        {channel.isPrivate && (
                          <span className="ml-2 text-xs text-muted-foreground">private</span>
                        )}
                      </span>
                      {can('manage_channels') && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={`Delete ${channel.name}`}
                          onClick={() => setChannelToDelete(channel)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {can('manage_channels') && (
                <Button type="button" onClick={() => setCreateChannelOpen(true)}>
                  Create channel
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invites">
          <Card>
            <CardContent className="pt-6">
              <GuildInviteManager
                invites={invites}
                loading={invitesLoading}
                onCreate={createInvite}
                onRevoke={revokeInvite}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {can('ban_members') && (
          <TabsContent value="bans">
            <Card>
              <CardContent className="pt-6">
                {bansError ? (
                  <p className="text-sm text-destructive">{bansError}</p>
                ) : (
                  <GuildBanList bans={bans} loading={bansLoading} onUnban={handleUnban} />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <GuildMemberRolesDialog
        member={rolesTarget}
        roles={roles}
        assignedRoleIds={assignedRoleIds}
        onClose={() => setRolesTarget(null)}
        onAssign={assignRole}
        onUnassign={unassignRole}
      />

      <CreateGuildChannelDialog
        open={createChannelOpen}
        onOpenChange={setCreateChannelOpen}
        onCreate={createChannel}
      />

      <AlertDialog
        open={channelToDelete !== null}
        onOpenChange={(open) => {
          if (!open && !isDeletingChannel) setChannelToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete #{channelToDelete?.name ?? 'channel'}?</AlertDialogTitle>
            <AlertDialogDescription>
              The channel and its message history are removed for every member. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingChannel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDeleteChannel();
              }}
              disabled={isDeletingChannel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingChannel ? 'Deleting…' : 'Delete channel'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmDeleteOpen}
        onOpenChange={(open) => {
          if (!isDeleting) setConfirmDeleteOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{guild.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This archives the guild for everyone. Members lose access, active invites are revoked,
              and the guild is removed from discovery. This cannot be undone from the app.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting…' : 'Delete guild'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
